import cx from 'classnames';
import {
  useCallback,
  useState,
  type FunctionComponent,
  type ReactElement,
} from 'react';
import { FormattedMessage } from 'react-intl';

import { ActionCopyLink } from './ActionCopyLink';
import { ActionFooter } from './ActionFooter';
import { ActionTimestamp } from './ActionTimestamp';
import { useActionContext } from './useActionContext';

import * as styles from './ActionDisplay.module.less';

interface ActionDisplayProps {
  actionId: string;
  messageId: string;
  date: string;
  substitutions: { [key: string]: ReactElement | string };
  isHighlighted?: boolean;
  appCreator?: {
    id?: string | null;
    name?: string | null;
  } | null;
  onReply?: () => void;
  cardShortUrl?: string;
  isRepliableAddAttachmentAction: boolean;
  boardName?: string;
}

export const ActionDisplay: FunctionComponent<ActionDisplayProps> = ({
  actionId,
  appCreator,
  messageId,
  date,
  substitutions: { attachmentPreview, ...substitutions },
  isHighlighted,
  onReply,
  cardShortUrl,
  isRepliableAddAttachmentAction,
  boardName,
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const { location } = useActionContext();

  // We need to remove this key so it doesn't get rendered as a string substitution.
  // If we don't do this, the image displays incorrectly above the timestamp.
  // If we parse it out above in Action and pass it in as a parameter, it won't render in here, so we're parsing it out here instead.
  substitutions.attachmentPreview = '';

  const actionLink = `${cardShortUrl}#action-${actionId}`;

  const onCopyLink = useCallback(() => {
    navigator.clipboard.writeText(actionLink);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 5000);
  }, [actionLink]);

  return (
    <div
      className={cx({
        [styles.wrapper]: true,
        [styles.boardActionWrapper]: ['board', 'member'].includes(location),
      })}
    >
      <div>
        <FormattedMessage
          id={messageId}
          defaultMessage="Action"
          description="Description of an action on a card back or in the board menu"
          values={substitutions}
        />
        <div className={styles.descriptionWrapper}>
          <ActionTimestamp
            onClickCallback={onCopyLink}
            date={date}
            href={`#action-${actionId}`}
            isHighlighted={isHighlighted}
            appCreator={appCreator}
            inline={isRepliableAddAttachmentAction}
          />
          {location === 'card' && (
            <div className={styles.copyLinkWrapper}>
              <ActionCopyLink
                isCopied={isCopied}
                onCopyLink={onCopyLink}
                type="action"
              />
            </div>
          )}
          {location === 'member' && <ActionFooter />}
        </div>
      </div>
      {attachmentPreview}
      {isRepliableAddAttachmentAction && location === 'card' && (
        <a
          className={styles.replyButton}
          href="#"
          onClick={onReply}
          role="button"
        >
          <FormattedMessage
            id="templates.action.reply"
            defaultMessage="Reply"
            description="Text on link to reply to an attachment"
          />
        </a>
      )}
    </div>
  );
};
